// Import Parse correctly without the /react-native suffix
// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
const Parse = require('parse');
import { PARSE_APP_ID, PARSE_JS_KEY, PARSE_SERVER_URL, PARSE_REST_API_KEY } from '@env';

// Web environment compatibility
const isWebEnvironment = typeof window !== 'undefined' && typeof document !== 'undefined';

// Environment variable validation
const validateEnvironmentVariables = () => {
  const vars = {
    PARSE_APP_ID,
    PARSE_JS_KEY,
    PARSE_SERVER_URL,
    PARSE_REST_API_KEY
  };

  const missing = Object.entries(vars)
    .filter(([key, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    console.error('Missing environment variables:', missing);
    console.error('Available vars:', Object.entries(vars).map(([key, value]) => `${key}: ${value ? 'SET' : 'NOT SET'}`));
    return false;
  }

  return true;
};

// Initialize Parse for web environment
const initializeParseForWeb = () => {
  if (!isWebEnvironment) return;

  try {
    // For web environments, we need to ensure Parse is properly initialized
    if (!validateEnvironmentVariables()) {
      throw new Error('Environment variables not properly configured');
    }

    // Initialize Parse for web
    if (!Parse.applicationId) {
      Parse.initialize(PARSE_APP_ID, PARSE_JS_KEY);
      Parse.serverURL = PARSE_SERVER_URL;
      console.log('Parse initialized for web environment');
    }

    // Make Parse globally available for debugging
    if (typeof window !== 'undefined') {
      (window as any).Parse = Parse;
    }
  } catch (error) {
    console.error('Failed to initialize Parse for web:', error);
  }
};

// Call initialization immediately
initializeParseForWeb();

// Function to get or validate Parse instance
const getParseInstance = async () => {
  try {
    // For web environments, ensure Parse is properly set up
    if (isWebEnvironment && !Parse.applicationId) {
      initializeParseForWeb();
    }

    // Check if Parse is properly initialized - Assuming initialization happens in index.ts
    if (!Parse.applicationId || !Parse.javaScriptKey || !Parse.serverURL) {
      // Log an error if Parse isn't initialized as expected from index.ts
      console.error('Parse SDK not initialized correctly. Please ensure initialization occurs in index.ts using environment variables.');
      throw new Error('Parse SDK not initialized.');
    }

    // Return the already initialized Parse instance
    return Parse;
  } catch (error) {
    console.error('Error getting Parse instance:', error);
    throw new Error('Failed to get Parse SDK instance: ' + (error instanceof Error ? error.message : String(error)));
  }
};

// Helper function to check if error is due to invalid session token
const isInvalidSessionError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString();
  const errorCode = error.code;
  
  // Check for common invalid session token indicators
  return (
    errorMessage.includes('Invalid session token') ||
    errorMessage.includes('invalid session token') ||
    errorCode === 209 || // Parse Error Code 209: Invalid session token
    errorCode === 101 || // Parse Error Code 101: Object not found (sometimes used for session issues)
    errorMessage.includes('session token')
  );
};

// Helper function to handle session token errors
const handleSessionTokenError = async (error: any) => {
  if (isInvalidSessionError(error)) {
    console.warn('Invalid session token detected, logging out user');
    
    try {
      // Clear the current user session
      const Parse = await getParseInstance();
      await Parse.User.logOut();
      
      // Clear any stored session data
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem('parse_session');
        window.localStorage.removeItem('parse_user');
      }
      
      // Dispatch a custom event to notify the app of session expiration
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('sessionExpired'));
      }
    } catch (logoutError) {
      console.error('Error during session cleanup:', logoutError);
    }
    
    // Throw a more user-friendly error
    throw new Error('Your session has expired. Please log in again.');
  }
  
  // Re-throw the original error if it's not a session token issue
  throw error;
};

const ADMIN_USER_TYPES = ['admin', 'super_admin'];
const AUDIT_LIMITS = {
  maxStringLength: 2000,
  maxArrayLength: 20,
  maxObjectKeys: 50,
  maxDepth: 3,
};
const AUDIT_KEY_OVERRIDES: Record<string, { maxStringLength?: number }> = {
  description: { maxStringLength: 5000 },
  adminNotes: { maxStringLength: 3000 },
};
const normalizeDateValue = (value: any) => {
  if (!value) return value;
  if (value instanceof Date) return value.toISOString();
  if (value.iso) return value.iso;
  return value;
};

const sanitizeAuditValue = (
  value: any,
  depth = 0,
  keyHint?: string,
): { value: any; truncated: boolean } => {
  if (value === null || value === undefined) return { value, truncated: false };
  if (depth >= AUDIT_LIMITS.maxDepth) return { value: '[truncated]', truncated: true };

  if (typeof value === 'string') {
    const override = keyHint ? AUDIT_KEY_OVERRIDES[keyHint] : undefined;
    const limit = override?.maxStringLength ?? AUDIT_LIMITS.maxStringLength;
    if (value.length <= limit) return { value, truncated: false };
    return { value: `${value.slice(0, limit)}…`, truncated: true };
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return { value, truncated: false };
  }

  if (Array.isArray(value)) {
    const truncated = value.length > AUDIT_LIMITS.maxArrayLength;
    const items = value.slice(0, AUDIT_LIMITS.maxArrayLength).map((item) => {
      const sanitized = sanitizeAuditValue(item, depth + 1, keyHint);
      return sanitized.value;
    });
    return { value: items, truncated };
  }

  if (value instanceof Date) {
    return { value: value.toISOString(), truncated: false };
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    const truncated = entries.length > AUDIT_LIMITS.maxObjectKeys;
    const limitedEntries = entries.slice(0, AUDIT_LIMITS.maxObjectKeys);
    const sanitizedObject = limitedEntries.reduce<Record<string, any>>((acc, [key, val]) => {
      const sanitized = sanitizeAuditValue(val, depth + 1, key);
      acc[key] = sanitized.value;
      return acc;
    }, {});
    if (truncated) {
      sanitizedObject.__truncated = true;
    }
    return { value: sanitizedObject, truncated };
  }

  return { value: String(value), truncated: true };
};

const sanitizeAuditPayload = (payload: any) => {
  if (!payload) return { payload, truncated: false };
  const sanitized = sanitizeAuditValue(payload);
  return { payload: sanitized.value, truncated: sanitized.truncated };
};

const getCurrentAdminActor = async () => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();
    if (!currentUser) return null;
    const userType = currentUser.get('userType');
    if (!ADMIN_USER_TYPES.includes(userType)) return null;
    return {
      id: currentUser.id,
      username: currentUser.get('username') || currentUser.get('email') || 'unknown',
      userType,
    };
  } catch (error) {
    console.warn('Failed to resolve current admin actor:', error);
    return null;
  }
};

const logAdminActivity = async (entry: {
  action: string;
  targetType: string;
  targetId?: string | null;
  summary: string;
  previousData?: Record<string, any> | null;
  newData?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
}) => {
  try {
    const actor = await getCurrentAdminActor();
    if (!actor) return; // only admins log; the cloud function re-verifies server-side

    const summary =
      entry.summary.length > AUDIT_LIMITS.maxStringLength
        ? `${entry.summary.slice(0, AUDIT_LIMITS.maxStringLength)}…`
        : entry.summary;
    const sanitizedPrevious = sanitizeAuditPayload(entry.previousData);
    const sanitizedNew = sanitizeAuditPayload(entry.newData);
    const sanitizedMetadata = sanitizeAuditPayload(entry.metadata);

    const payload: Record<string, any> = {
      action: entry.action,
      targetType: entry.targetType,
      targetId: entry.targetId || null,
      summary,
      previousData: sanitizedPrevious.payload || null,
      newData: sanitizedNew.payload || null,
      metadata: sanitizedMetadata.payload || null,
    };

    if (sanitizedPrevious.truncated || sanitizedNew.truncated || sanitizedMetadata.truncated) {
      payload.metadata = {
        ...(payload.metadata || {}),
        truncated: true,
      };
    }

    // The AdminActivity record — including the actor pointer — is written server-side with the
    // master key. The cloud function derives the actor from the caller's session token, so the
    // logged actor cannot be spoofed from here.
    const Parse = await getParseInstance();
    await Parse.Cloud.run('logAdminActivity', payload);
  } catch (error) {
    console.warn('Admin activity logging failed:', error);
  }
};

const serializeOpportunityForAudit = (opportunity: any) => {
  const attrs = opportunity?.attributes || opportunity || {};
  const fields = [
    'title',
    'description',
    'type',
    'organization',
    'startDate',
    'endDate',
    'applicationOpenDate',
    'applicationCloseDate',
    'deadline',
    'location',
    'eligibility',
    'website',
    'tags',
    'isClaimed',
    'isYearlyRecurring',
    'gradeLevel',
    'interactionTypes',
    'status',
    'cost',
    'createdBy',
    'isHiddenFromSearch',
  ];

  return fields.reduce((acc: Record<string, any>, key) => {
    if (attrs[key] !== undefined) {
      acc[key] = normalizeDateValue(attrs[key]);
    }
    return acc;
  }, {});
};

const buildOpportunityDelta = (attrs: Record<string, any>, data: Record<string, any>) => {
  const previousData: Record<string, any> = {};
  const newData: Record<string, any> = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return;
    previousData[key] = normalizeDateValue(attrs[key]);
    newData[key] = normalizeDateValue(value);
  });

  return { previousData, newData };
};

// Test connection to Parse server
export const testConnection = async (): Promise<boolean> => {
  try {
    const Parse = await getParseInstance();
    
    // Create a test object
    const TestObject = Parse.Object.extend('TestObject');
    const testObject = new TestObject();
    testObject.set('message', 'Hello from React Native!');
    
    // Save the object
    await testObject.save();
    console.log('Parse connection successful!');
    return true;
  } catch (error) {
    console.error('Error testing Parse connection:', error);
    return false;
  }
};

// User Authentication
export const signUp = async (username: string, email: string, password: string, userType: string = 'regular'): Promise<any> => {
  try {
    const Parse = await getParseInstance();
    
    // Create a new user
    const user = new Parse.User();
    user.set('username', username);
    user.set('email', email);
    user.set('password', password);
    
    // Additional fields if needed
    user.set('userType', userType); // Can be 'regular', 'admin', 'host', etc.
    
    // Sign up user
    const result = await user.signUp();
    console.log('User created successfully with id:', result.id);
    return result;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

export const logIn = async (username: string, password: string): Promise<any> => {
  try {
    const Parse = await getParseInstance();
    
    // Log in user
    const user = await Parse.User.logIn(username, password);
    console.log('User logged in successfully:', user.id);
    return user;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const logOut = async (): Promise<void> => {
  try {
    const Parse = await getParseInstance();
    
    // Log out current user
    await Parse.User.logOut();
    console.log('User logged out successfully');
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

export const getCurrentUser = async (): Promise<any> => {
  try {
    const Parse = await getParseInstance();
    
    // Get current user
    const currentUser = Parse.User.current();
    return currentUser;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Opportunity Management
export const createOpportunity = async (data: {
  title: string;
  description: string;
  type: string;
  organization: string;
  startDate?: Date | string;
  endDate?: Date | string;
  applicationOpenDate?: Date | string;
  applicationCloseDate?: Date | string;
  deadline?: Date | string;
  location: string;
  eligibility?: string;
  website?: string;
  tags?: string[];
  isClaimed?: boolean;
  isYearlyRecurring?: boolean;
  gradeLevel?: string[];
  status: string;
  cost?: string;
}) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const opportunity = new Opportunity();
    
    // Set all the opportunity fields
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        // Convert string dates to actual Date objects
        if (['startDate', 'endDate', 'deadline', 'applicationOpenDate', 'applicationCloseDate'].includes(key) && typeof value === 'string') {
          try {
            opportunity.set(key, new Date(value));
          } catch (e) {
            console.error(`Invalid date for ${key}:`, value);
            // Don't set this field if invalid
          }
        } else {
          opportunity.set(key, value);
        }
      }
    });
    
    // Add timestamps
    opportunity.set('createdAt', new Date());
    opportunity.set('updatedAt', new Date());
    
    // Save the opportunity without master key
    const result = await opportunity.save();
    console.log('Opportunity created with objectId:', result.id);

    await logAdminActivity({
      action: 'opportunity.create',
      targetType: 'opportunity',
      targetId: result.id,
      summary: `Created opportunity "${data.title}"`,
      previousData: null,
      newData: serializeOpportunityForAudit(result),
    });

    return result;
  } catch (error) {
    console.error('Error creating opportunity:', error);
    throw error;
  }
};

export const getOpportunities = async (limit = 100) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);
    
    // Filter out opportunities that are hidden from search
    query.notEqualTo('isHiddenFromSearch', true);
    
    // Only set limit without default sorting
    query.limit(limit);
    
    try {
      // Fetch without master key
      const results = await query.find();
      
      // If no opportunities exist, create sample data
      if (results.length === 0) {
        console.log('No opportunities found, will create sample data');
        await createSampleOpportunities();
        
        // Query again to get the newly created opportunities (excluding hidden ones)
        const newQuery = new Parse.Query(Opportunity);
        newQuery.notEqualTo('isHiddenFromSearch', true);
        newQuery.limit(limit);
        return await newQuery.find();
      }
      
      return results;
    } catch (error) {
      console.error('Standard query failed:', error);
      await handleSessionTokenError(error);
    }
  } catch (error) {
    console.error('Error fetching opportunities:', error);
    await handleSessionTokenError(error);
  }
};

// Admin function to get all opportunities including hidden ones
export const getAllOpportunitiesForAdmin = async (limit = 100) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);
    
    // Admin can see all opportunities, including hidden ones
    // No filtering by isHiddenFromSearch
    
    // Only set limit without default sorting
    query.limit(limit);
    
    try {
      // Fetch without master key
      const results = await query.find();
      
      // If no opportunities exist, create sample data
      if (results.length === 0) {
        console.log('No opportunities found, will create sample data');
        await createSampleOpportunities();
        
        // Query again to get the newly created opportunities
        const newQuery = new Parse.Query(Opportunity);
        newQuery.limit(limit);
        return await newQuery.find();
      }
      
      return results;
    } catch (error) {
      console.error('Standard query failed:', error);
      await handleSessionTokenError(error);
    }
  } catch (error) {
    console.error('Error fetching all opportunities for admin:', error);
    await handleSessionTokenError(error);
  }
};

/**
 * Creates sample opportunities if none exist.
 * This is called automatically by getOpportunities if no opportunities are found.
 */
export const createSampleOpportunities = async () => {
  try {
    console.log('Creating sample opportunities...');
    
    const sampleOpportunities = [
      {
        title: 'BC Science Fair',
        description: 'Annual science fair for students across British Columbia to showcase their scientific projects and innovations.',
        type: 'Competition',
        status: 'Open',
        startDate: new Date('2025-05-10'),
        endDate: new Date('2025-05-12'),
        applicationOpenDate: new Date('2025-01-15'),
        applicationCloseDate: new Date('2025-04-15'),
        location: 'Vancouver, BC',
        organization: 'Science Fair Foundation BC',
        website: 'https://example.com/bcsciencefair',
        eligibility: 'Students in grades 7-12 in British Columbia',
        deadline: new Date('2025-04-15'),
        cost: 'Free',
        tags: ['Science', 'Research', 'K-12', 'Competition'],
        gradeLevel: ['Middle School', 'High School'],
        isYearlyRecurring: true,
        isClaimed: false
      },
      {
        title: 'UBC Quantum Computing Hackathon',
        description: 'A weekend-long hackathon focused on quantum computing applications and challenges.',
        type: 'Hackathon',
        status: 'Coming Soon',
        startDate: new Date('2025-06-20'),
        endDate: new Date('2025-06-22'),
        applicationOpenDate: new Date('2025-05-01'),
        applicationCloseDate: new Date('2025-06-10'),
        location: 'University of British Columbia, Vancouver',
        organization: 'UBC Quantum Computing Club',
        website: 'https://example.com/ubcquantumhackathon',
        eligibility: 'University students and recent graduates',
        deadline: new Date('2025-06-10'),
        cost: 'Free',
        tags: ['Quantum Computing', 'Programming', 'University', 'Hackathon'],
        gradeLevel: ['Undergraduate', 'Graduate'],
        isYearlyRecurring: false,
        isClaimed: false
      },
      {
        title: 'BC STEM Innovation Grant',
        description: 'Funding opportunity for innovative STEM projects that benefit the British Columbia community.',
        type: 'Grant',
        status: 'Open',
        startDate: new Date('2025-01-01'),
        endDate: new Date('2025-12-31'),
        applicationOpenDate: new Date('2025-01-01'),
        applicationCloseDate: new Date('2025-09-30'),
        location: 'British Columbia',
        organization: 'BC Innovation Council',
        website: 'https://example.com/bcinnovationgrant',
        eligibility: 'BC residents with innovative STEM project ideas',
        deadline: new Date('2025-09-30'),
        cost: 'N/A',
        tags: ['Funding', 'Innovation', 'Research', 'Grant'],
        gradeLevel: ['All Levels'],
        isYearlyRecurring: true,
        isClaimed: false
      },
      {
        title: 'Women in Technology Scholarship',
        description: 'Scholarship for women pursuing careers in technology fields in British Columbia.',
        type: 'Scholarship',
        status: 'Open',
        startDate: new Date('2025-01-01'),
        endDate: new Date('2025-07-31'),
        applicationOpenDate: new Date('2025-01-01'),
        applicationCloseDate: new Date('2025-05-31'),
        location: 'British Columbia',
        organization: 'BC Tech Association',
        website: 'https://example.com/womenintechscholarship',
        eligibility: 'Women enrolled in technology programs at BC post-secondary institutions',
        deadline: new Date('2025-05-31'),
        cost: 'N/A',
        tags: ['Women in Tech', 'Education', 'Scholarship', 'Diversity'],
        gradeLevel: ['Undergraduate', 'Graduate'],
        isYearlyRecurring: true,
        isClaimed: false
      },
      {
        title: 'Vancouver Tech Conference',
        description: 'Annual technology conference featuring industry leaders, workshops, and networking opportunities.',
        type: 'Conference',
        status: 'Coming Soon',
        startDate: new Date('2025-10-15'),
        endDate: new Date('2025-10-17'),
        applicationOpenDate: new Date('2025-05-01'),
        applicationCloseDate: new Date('2025-09-30'),
        location: 'Vancouver Convention Centre',
        organization: 'Vancouver Tech Alliance',
        website: 'https://example.com/vancouvertechconference',
        eligibility: 'Open to all',
        deadline: new Date('2025-09-30'),
        cost: '$299 - $599',
        tags: ['Technology', 'Networking', 'Professional Development', 'Conference'],
        gradeLevel: ['All Levels'],
        isYearlyRecurring: true,
        isClaimed: true
      },
      {
        title: 'Elementary School Science Program',
        description: 'A series of interactive science workshops designed specifically for elementary school children.',
        type: 'Workshop',
        status: 'Open',
        startDate: new Date('2025-03-01'),
        endDate: new Date('2025-06-30'),
        applicationOpenDate: new Date('2025-01-15'),
        applicationCloseDate: new Date('2025-02-28'),
        location: 'Multiple locations across BC',
        organization: 'BC Science Education Foundation',
        website: 'https://example.com/elementaryscienceprogram',
        eligibility: 'Elementary schools in British Columbia',
        deadline: new Date('2025-02-28'),
        cost: '$50 per school',
        tags: ['Science', 'Education', 'Elementary', 'Workshop'],
        gradeLevel: ['Elementary'],
        isYearlyRecurring: true,
        isClaimed: false
      }
    ];
    
    try {
      // Try SDK first
      const Parse = await getParseInstance();
      const Opportunity = Parse.Object.extend('Opportunity');
      const savePromises = sampleOpportunities.map(data => {
        const opportunity = new Opportunity();
        Object.entries(data).forEach(([key, value]) => {
          opportunity.set(key, value);
        });
        return opportunity.save();
      });
      await Promise.all(savePromises);
      console.log(`Created ${sampleOpportunities.length} sample opportunities using Parse SDK`);

    } catch (parseError) {
      // Fallback to REST API if SDK fails
      console.error('Error creating opportunities using Parse SDK, falling back to REST API:', parseError);
      console.log('Attempting to create opportunities using direct REST API...');

      const createdOpportunities = [];
      for (const data of sampleOpportunities) {
        try {
          const formattedData: Record<string, any> = { ...data };
          for (const [key, value] of Object.entries(formattedData)) {
            if (value instanceof Date) {
              formattedData[key] = { __type: 'Date', iso: value.toISOString() };
            }
          }

          const response = await fetch(`${PARSE_SERVER_URL}/classes/Opportunity`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Parse-Application-Id': PARSE_APP_ID,
              'X-Parse-JavaScript-Key': PARSE_JS_KEY // public create gated by CLP
            },
            body: JSON.stringify(formattedData)
          });

          if (!response.ok) {
            const errorBody = await response.text();
            throw new Error(`HTTP error! Status: ${response.status}. Body: ${errorBody}`);
          }

          const result = await response.json();
          console.log(`Created opportunity '${data.title}' via REST with ID ${result.objectId}`);
          createdOpportunities.push(result);
        } catch (oppError) {
          console.error(`Error creating opportunity '${data.title}' via REST:`, oppError);
        }
      }
      console.log(`Created ${createdOpportunities.length} sample opportunities using REST API fallback`);
    }
  } catch (error) {
    console.error('Error creating sample opportunities:', error);
    throw error;
  }
};

export const getOpportunityById = async (id: string) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);
    
    const opportunity = await query.get(id);
    return opportunity;
  } catch (error) {
    console.error('Error fetching opportunity by ID:', error);
    throw error;
  }
};

/**
 * Delete an opportunity by ID - with existence check and multiple deletion approaches
 */
export const deleteOpportunity = async (id: string) => {
  console.log(`🗑️ Starting deletion for opportunity with ID: ${id}`);
  if (!id) {
      console.warn('deleteOpportunity called with invalid ID');
      return false;
  }

  try {
    // Capture a snapshot for the audit trail before the row is gone.
    let deletedSnapshot: Record<string, any> | null = null;
    try {
      const existing = await getOpportunityById(id);
      deletedSnapshot = serializeOpportunityForAudit(existing);
    } catch (snapshotError) {
      console.warn('Failed to capture opportunity snapshot before delete:', snapshotError);
    }

    // Delete with master-key privileges on the server. The cloud function verifies the caller is
    // an admin and is idempotent (an already-deleted opportunity is reported as success).
    const Parse = await getParseInstance();
    await Parse.Cloud.run('adminDeleteOpportunity', { id });

    await logAdminActivity({
      action: 'opportunity.delete',
      targetType: 'opportunity',
      targetId: id,
      summary: `Deleted opportunity "${deletedSnapshot?.title || id}"`,
      previousData: deletedSnapshot,
      newData: null,
    });

    console.log(`🗑️ Opportunity ${id} deleted.`);
    return true;
  } catch (error) {
    console.error(`🗑️ General error during deletion for opportunity ${id}:`, error);
    throw error; // Re-throw general errors
  }
};

/**
 * Update an existing opportunity
 */
export const updateOpportunity = async (id: string, data: any) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);
    
    const opportunity = await query.get(id);
    const attrs = opportunity.attributes || {};
    const { previousData, newData } = buildOpportunityDelta(attrs, data);
    
    // Update fields from data
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        // Handle date conversions
        if (['startDate', 'endDate', 'deadline', 'applicationOpenDate', 'applicationCloseDate'].includes(key)) {
          if (value === null) {
            // Explicitly clear the date field
            opportunity.unset(key);
          } else if (typeof value === 'string') {
            try {
              opportunity.set(key, new Date(value));
            } catch (e) {
              console.error(`Invalid date for ${key}:`, value);
              // Don't set this field if invalid
            }
          } else if (value instanceof Date) {
            opportunity.set(key, value);
          }
        } else {
          opportunity.set(key, value);
        }
      }
    });
    
    // Always update the updatedAt timestamp
    opportunity.set('updatedAt', new Date());
    
    // Save the updated opportunity
    const result = await opportunity.save(null);
    console.log(`Opportunity with ID ${id} updated successfully`);

    if (Object.keys(previousData).length > 0) {
      await logAdminActivity({
        action: 'opportunity.update',
        targetType: 'opportunity',
        targetId: id,
        summary: `Updated opportunity "${result.get('title') || 'Untitled'}"`,
        previousData,
        newData,
      });
    }

    return result;
  } catch (error) {
    console.error('Error updating opportunity:', error);
    throw error;
  }
};

const updateOpportunityWithMasterKey = async (id: string, data: any) => {
  // Master-key update applied server-side (admin verified in the cloud function). Used as the
  // revert fallback when a normal session-scoped update is rejected by ACLs. The cloud function
  // accepts Date / ISO-string / {__type:'Date'} values and treats null as "unset this field".
  const Parse = await getParseInstance();
  await Parse.Cloud.run('adminUpdateOpportunity', { id, data });
};

// User favorites
export const saveOpportunity = async (opportunityId: string) => {
  try {
    const Parse = await getParseInstance();
    
    // Get current user
    const currentUser = Parse.User.current();
    if (!currentUser) {
      throw new Error('No user is currently logged in');
    }
    
    // Create UserFavorite object
    const UserFavorite = Parse.Object.extend('UserFavorite');
    const userFavorite = new UserFavorite();
    
    // Set the user and opportunity
    userFavorite.set('user', currentUser);
    
    // Create a pointer to the opportunity
    const Opportunity = Parse.Object.extend('Opportunity');
    const opportunityPointer = new Opportunity();
    opportunityPointer.id = opportunityId;
    userFavorite.set('opportunity', opportunityPointer);
    
    // Save the favorite
    const result = await userFavorite.save();
    console.log('Opportunity saved successfully:', result.id);
    return result;
  } catch (error) {
    console.error('Error saving opportunity:', error);
    await handleSessionTokenError(error);
  }
};

export const unsaveOpportunity = async (opportunityId: string) => {
  try {
    const Parse = await getParseInstance();
    
    // Get current user
    const currentUser = Parse.User.current();
    if (!currentUser) {
      throw new Error('No user is currently logged in');
    }
    
    // Find the user favorite
    const UserFavorite = Parse.Object.extend('UserFavorite');
    const query = new Parse.Query(UserFavorite);
    
    // Query for the specific favorite
    query.equalTo('user', currentUser);
    
    // Create a pointer to the opportunity
    const Opportunity = Parse.Object.extend('Opportunity');
    const opportunityPointer = new Opportunity();
    opportunityPointer.id = opportunityId;
    query.equalTo('opportunity', opportunityPointer);
    
    // Find and delete the favorite
    const userFavorite = await query.first();
    if (userFavorite) {
      await userFavorite.destroy();
      console.log('Opportunity unsaved successfully');
      return true;
    } else {
      console.log('No saved opportunity found to remove');
      return false;
    }
  } catch (error) {
    console.error('Error unsaving opportunity:', error);
    await handleSessionTokenError(error);
  }
};

export const getSavedOpportunities = async () => {
  try {
    const Parse = await getParseInstance();
    
    // Get current user
    const currentUser = Parse.User.current();
    if (!currentUser) {
      throw new Error('No user is currently logged in');
    }
    
    // Find all user favorites
    const UserFavorite = Parse.Object.extend('UserFavorite');
    const query = new Parse.Query(UserFavorite);
    
    // Query for current user's favorites
    query.equalTo('user', currentUser);
    
    // Include the full opportunity object
    query.include('opportunity');
    
    // Execute query
    const results = await query.find();
    
    // Extract opportunities from results, filtering out any null opportunities
    const opportunities = results
      .map((result: any) => result.get('opportunity'))
      .filter((opportunity: any) => opportunity !== null && opportunity !== undefined);
      
    return opportunities;
  } catch (error) {
    console.error('Error fetching saved opportunities:', error);
    await handleSessionTokenError(error);
  }
};

export const isOpportunitySaved = async (opportunityId: string): Promise<boolean> => {
  try {
    const Parse = await getParseInstance();
    
    // Get current user
    const currentUser = Parse.User.current();
    if (!currentUser) {
      return false; // Not logged in, so not saved
    }
    
    // Find the user favorite
    const UserFavorite = Parse.Object.extend('UserFavorite');
    const query = new Parse.Query(UserFavorite);
    
    // Query for the specific favorite
    query.equalTo('user', currentUser);
    
    // Create a pointer to the opportunity
    const Opportunity = Parse.Object.extend('Opportunity');
    const opportunityPointer = new Opportunity();
    opportunityPointer.id = opportunityId;
    query.equalTo('opportunity', opportunityPointer);
    
    // Check if exists
    const count = await query.count();
    return count > 0;
  } catch (error) {
    console.error('Error checking if opportunity is saved:', error);
    await handleSessionTokenError(error);
    return false;
  }
};

// Import opportunities from local data to Back4App
export const importOpportunitiesToParse = async (opportunities: any[]) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const opportunitiesToSave = [];
    
    // Process each opportunity individually to handle errors better
    for (const opp of opportunities) {
      try {
        const opportunity = new Opportunity();
        
        // Set each field individually to better handle errors
        Object.entries(opp).forEach(([key, value]) => {
          // Skip the id field as Parse will generate one
          if (key === 'id') return;
          
          // Skip fields with null or undefined values
          if (value === null || value === undefined) return;
          
          try {
            opportunity.set(key, value);
          } catch (fieldError) {
            console.error(`Error setting field ${key} for opportunity "${opp.title}":`, fieldError);
            // Skip this field but continue with the rest
          }
        });
        
        opportunitiesToSave.push(opportunity);
      } catch (oppError) {
        console.error(`Error processing opportunity "${opp.title}":`, oppError);
        // Skip this opportunity but continue with the rest
      }
    }
    
    // Save all valid opportunities
    if (opportunitiesToSave.length === 0) {
      throw new Error('No valid opportunities to save');
    }
    
    // Save in smaller batches to prevent potential issues
    const batchSize = 20;
    const savedOpportunities = [];
    
    for (let i = 0; i < opportunitiesToSave.length; i += batchSize) {
      const batch = opportunitiesToSave.slice(i, i + batchSize);
      console.log(`Saving batch ${i / batchSize + 1} of ${Math.ceil(opportunitiesToSave.length / batchSize)}...`);
      
      try {
        const saved = await Parse.Object.saveAll(batch);
        savedOpportunities.push(...saved);
      } catch (batchError) {
        console.error(`Error saving batch ${i / batchSize + 1}:`, batchError);
        // Try to save individual opportunities in the batch
        for (const opp of batch) {
          try {
            const saved = await opp.save();
            savedOpportunities.push(saved);
          } catch (saveError) {
            console.error(`Error saving individual opportunity:`, saveError);
          }
        }
      }
    }
    
    console.log(`${savedOpportunities.length} opportunities imported successfully`);
    return savedOpportunities;
  } catch (error) {
    console.error('Error importing opportunities:', error);
    throw error;
  }
};

/**
 * Issue Reports
 * Functions to handle reporting issues with opportunities
 */

/**
 * Create a new issue report with enhanced error handling and web compatibility
 */
export const reportIssue = async (data: {
  opportunityId?: string;
  issueType: 'Incorrect Dates' | 'Invalid Information' | 'Broken Link' | 'Other';
  description: string;
  contactEmail?: string;
}) => {
  try {
    // Validate input data
    if (!data.issueType || !data.description) {
      throw new Error('Missing required fields: issueType and description are required');
    }

    // Try SDK approach first
    try {
      const Parse = await getParseInstance();
      
      // Create Issue Report object
      const IssueReport = Parse.Object.extend('IssueReport');
      const report = new IssueReport();
      
      // Set basic issue data
      report.set('issueType', data.issueType);
      report.set('description', data.description);
      report.set('status', 'Pending');
      
      // Set optional fields if provided
      if (data.contactEmail) {
        report.set('contactEmail', data.contactEmail);
      }
      
      // If associated with an opportunity, create a pointer
      if (data.opportunityId) {
        const Opportunity = Parse.Object.extend('Opportunity');
        const opportunityPointer = new Opportunity();
        opportunityPointer.id = data.opportunityId;
        report.set('opportunity', opportunityPointer);
      }
      
      // Set timestamps
      const now = new Date();
      report.set('createdAt', now);
      report.set('updatedAt', now);
      
      // Save the report
      const result = await report.save();
      console.log('Issue report created successfully:', result.id);
      return result;

    } catch (sdkError) {
      console.warn('SDK approach failed, trying REST API:', sdkError);
      
      // If SDK fails, try REST API approach
      if (!validateEnvironmentVariables()) {
        throw new Error('Environment variables not configured for REST API fallback');
      }

      const reportData: any = {
        issueType: data.issueType,
        description: data.description,
        status: 'Pending',
        createdAt: { __type: 'Date', iso: new Date().toISOString() },
        updatedAt: { __type: 'Date', iso: new Date().toISOString() }
      };

      if (data.contactEmail) {
        reportData.contactEmail = data.contactEmail;
      }

      if (data.opportunityId) {
        reportData.opportunity = {
          __type: 'Pointer',
          className: 'Opportunity',
          objectId: data.opportunityId
        };
      }

      const response = await fetch(`${PARSE_SERVER_URL}/classes/IssueReport`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Parse-Application-Id': PARSE_APP_ID,
          'X-Parse-JavaScript-Key': PARSE_JS_KEY
        },
        body: JSON.stringify(reportData)
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`REST API error: ${response.status} - ${errorBody}`);
      }

      const result = await response.json();
      console.log('Issue report created successfully via REST API:', result.objectId);
      
      // Return a Parse-like object for consistency
      return {
        id: result.objectId,
        get: (key: string) => result[key],
        ...result
      };
    }
  } catch (error) {
    console.error('Failed to create issue report:', error);
    
    // Provide more specific error messages
    let errorMessage = 'Failed to submit issue report';
    
    if (error instanceof Error) {
      if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'Network connection error';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'Browser security restriction';
      } else if (error.message.includes('Parse')) {
        errorMessage = 'Server configuration error';
      } else if (error.message.includes('Environment')) {
        errorMessage = 'Application configuration error';
      }
    }
    
    throw new Error(`${errorMessage}: ${error instanceof Error ? error.message : String(error)}`);
  }
};

/**
 * Get issue reports (for admin use)
 */
export const getIssueReports = async (status?: 'Pending' | 'Reviewed' | 'Resolved' | 'Rejected') => {
  try {
    // Admin-only read with the master key, performed server-side. Returns Parse objects (with the
    // opportunity pointer included) — the SDK rehydrates them on this side.
    const Parse = await getParseInstance();
    const results = await Parse.Cloud.run('getIssueReports', status ? { status } : {});
    console.log(`📊 Loaded ${results?.length || 0} issue reports`);
    return results || [];
  } catch (error) {
    console.error('📊 Failed to fetch issue reports:', error);
    return []; // Return empty array on failure
  }
};

/**
 * Update issue report status (for admin use)
 */
export const updateIssueReportStatus = async (
  reportId: string, 
  status: 'Pending' | 'Reviewed' | 'Resolved' | 'Rejected',
  adminNotes?: string
) => {
  try {
    // Update applied server-side with the master key; returns the prior values for the audit trail.
    const Parse = await getParseInstance();
    const { previousStatus, previousAdminNotes } = await Parse.Cloud.run('updateIssueReportStatus', {
      reportId,
      status,
      adminNotes,
    });
    console.log(`Issue report ${reportId} status updated to ${status}`);

    await logAdminActivity({
      action: 'issue.updateStatus',
      targetType: 'issue',
      targetId: reportId,
      summary: `Updated issue status to ${status}`,
      previousData: { status: previousStatus, adminNotes: previousAdminNotes },
      newData: { status, adminNotes: adminNotes || previousAdminNotes },
    });

    return true;
  } catch (error) {
    console.error('Error updating issue report status:', error);
    throw error;
  }
};

/**
 * Check if an opportunity exists by ID
 */
export const opportunityExists = async (id: string): Promise<boolean> => {
  console.log(`🔍 Checking if opportunity with ID ${id} exists`);
  if (!id) {
      console.warn('🔍 opportunityExists called with null or empty ID');
      return false;
  }
  try {
    // Opportunities are publicly readable, so a REST-API-key read is enough to check existence.
    // Fetch only objectId to minimize data transfer.
    const response = await fetch(`${PARSE_SERVER_URL}/classes/Opportunity/${id}?keys=objectId`, {
      method: 'GET',
      headers: {
        'X-Parse-Application-Id': PARSE_APP_ID,
        'X-Parse-REST-API-Key': PARSE_REST_API_KEY
      }
    });
    console.log(`🔍 Existence check (REST) response status:`, response.status);
    if (response.status === 200) {
      console.log(`🔍 Opportunity ${id} exists (checked via REST)`);
      return true;
    } else if (response.status === 404) {
      console.log(`🔍 Opportunity ${id} does not exist (checked via REST - 404)`);
      return false;
    } else {
      console.warn(`🔍 REST existence check returned status ${response.status}. Falling back to SDK.`);
    }

    // Approach 2: Fallback to Parse SDK 'get' method (catches Parse Error 101)
    try {
        const Parse = await getParseInstance();
        const Opportunity = Parse.Object.extend('Opportunity');
        const query = new Parse.Query(Opportunity);
        await query.get(id);
        console.log(`🔍 Opportunity ${id} exists (checked via SDK get)`);
        return true;
    } catch (sdkError: any) {
        if (sdkError.code === Parse.Error.OBJECT_NOT_FOUND) {
            console.log(`🔍 Opportunity ${id} does not exist (checked via SDK get - Object Not Found)`);
            return false;
        }
        console.error(`🔍 SDK existence check failed with unexpected error:`, sdkError);
        return false; // Assume non-existence on other SDK errors
    }

  } catch (error) {
    console.error(`🔍 Error during existence check for opportunity ${id}:`, error);
    return false; // Assume non-existence on general errors
  }
};

/**
 * Create a new opportunity suggestion
 */
export const suggestOpportunity = async (data: {
  title: string;
  description: string;
  contactEmail?: string;
  link?: string;
}) => {
  try {
    const Parse = await getParseInstance();
    const Suggestion = Parse.Object.extend('OpportunitySuggestion');
    const suggestion = new Suggestion();

    suggestion.set('title', data.title);
    suggestion.set('description', data.description);
    suggestion.set('status', 'Pending');
    if (data.contactEmail) suggestion.set('contactEmail', data.contactEmail);
    if (data.link) suggestion.set('link', data.link);
    
    const result = await suggestion.save();
    console.log('Opportunity suggestion created:', result.id);
    return result;
  } catch (error) {
    console.error('Error creating opportunity suggestion:', error);
    throw error;
  }
};

/**
 * Get opportunity suggestions (for admin use)
 */
export const getOpportunitySuggestions = async (
  status?: 'Pending' | 'Reviewed' | 'Accepted' | 'Rejected'
) => {
  try {
    // Admin-only read with the master key, performed server-side. Returns Parse objects.
    const Parse = await getParseInstance();
    const results = await Parse.Cloud.run('getOpportunitySuggestions', status ? { status } : {});
    return results || [];
  } catch (error) {
    console.error('Error fetching opportunity suggestions:', error);
    return [];
  }
};

/**
 * Update opportunity suggestion status (for admin use)
 */
export const updateOpportunitySuggestionStatus = async (
  id: string,
  status: 'Pending' | 'Reviewed' | 'Accepted' | 'Rejected',
  adminNotes?: string
) => {
  try {
    // Update applied server-side with the master key; returns the prior values for the audit trail.
    const Parse = await getParseInstance();
    const { previousStatus, previousAdminNotes } = await Parse.Cloud.run('updateOpportunitySuggestionStatus', {
      id,
      status,
      adminNotes,
    });
    console.log('Suggestion status updated:', id);

    await logAdminActivity({
      action: 'suggestion.updateStatus',
      targetType: 'suggestion',
      targetId: id,
      summary: `Updated suggestion status to ${status}`,
      previousData: { status: previousStatus, adminNotes: previousAdminNotes },
      newData: { status, adminNotes: adminNotes || previousAdminNotes },
    });

    return { id, status };
  } catch (error) {
    console.error('Error updating suggestion status:', error);
    throw error;
  }
};

// ---- Crawled Opportunities (automated crawler review queue) ----------------

/**
 * Get crawler-discovered opportunities awaiting review (for admin use).
 * Mirrors getOpportunitySuggestions: master-key REST read, returns Parse objects.
 */
export const getCrawledOpportunities = async (
  status?: 'Pending' | 'Approved' | 'Rejected'
) => {
  try {
    // Admin-only read with the master key, performed server-side. Returns Parse objects.
    const Parse = await getParseInstance();
    const results = await Parse.Cloud.run('getCrawledOpportunities', status ? { status } : {});
    return results || [];
  } catch (error) {
    console.error('Error fetching crawled opportunities:', error);
    return [];
  }
};

// CrawledOpportunity rows are written by the crawler with the master key. Admin review actions
// (approve/reject) read and update them through cloud functions that verify the caller is an admin
// and apply the master key on Back4App's servers. getCrawledOpportunityById returns the row as a
// plain record (toJSON) so approveCrawledOpportunity can merge edits before publishing.
const getCrawledOpportunityById = async (id: string): Promise<any> => {
  const Parse = await getParseInstance();
  return Parse.Cloud.run('getCrawledOpportunityById', { id });
};

const updateCrawledOpportunity = async (id: string, fields: Record<string, any>) => {
  const Parse = await getParseInstance();
  return Parse.Cloud.run('updateCrawledOpportunity', { id, fields });
};

/**
 * Approve a crawled opportunity: promote it to a live Opportunity via the existing
 * createOpportunity() path (keeping the audit trail), then mark the queue item Approved.
 *
 * `edits` lets the admin review screen override what the crawler extracted before it is
 * published. Keys mirror the CrawledOpportunity/Opportunity fields (e.g. title, description,
 * type as a string[], tags, dates as strings, isYearlyRecurring); any key not supplied falls
 * back to the crawled value. The edited fields are also written back onto the queue row so it
 * reflects exactly what was published, and listed in the audit log.
 */
export const approveCrawledOpportunity = async (
  id: string,
  reviewerId?: string,
  edits?: Record<string, any>
) => {
  try {
    const item = await getCrawledOpportunityById(id);
    // Start from the crawler's extraction, then layer any admin edits on top.
    const merged = { ...item, ...(edits || {}) };
    const typeVal = merged.type;
    const interactionTypes = Array.isArray(merged.interactionTypes) ? merged.interactionTypes : [];

    const created = await createOpportunity({
      title: merged.title,
      description: merged.description,
      type: Array.isArray(typeVal) ? typeVal.join(', ') : String(typeVal || 'Other'),
      organization: merged.organization || 'Unknown Organization',
      location: merged.location || 'Location TBD',
      // Never fall back to sourceUrl when the crawler flagged the link as unresolved (e.g. a
      // listicle/roundup): publishing the roundup URL as the opportunity's site is exactly the bug
      // this guards against. The admin is expected to have filled in `website` during review.
      website: merged.website || (merged.linkNeedsReview ? '' : merged.sourceUrl),
      eligibility: merged.eligibility,
      cost: merged.cost,
      tags: merged.tags || [],
      gradeLevel: merged.gradeLevel || [],
      interactionTypes: interactionTypes.length > 0 ? interactionTypes : undefined,
      startDate: merged.startDate,
      endDate: merged.endDate,
      applicationOpenDate: merged.applicationOpenDate,
      applicationCloseDate: merged.applicationCloseDate,
      isYearlyRecurring: !!merged.isYearlyRecurring,
      isClaimed: false,
      status: 'Open',
    } as any);

    const editedKeys = edits ? Object.keys(edits) : [];
    await updateCrawledOpportunity(id, {
      ...(edits || {}),
      status: 'Approved',
      reviewedAt: { __type: 'Date', iso: new Date().toISOString() },
      ...(reviewerId ? { reviewedBy: reviewerId } : {}),
      approvedOpportunityId: created.id,
    });

    await logAdminActivity({
      action: 'crawledOpportunity.approve',
      targetType: 'crawledOpportunity',
      targetId: id,
      summary: editedKeys.length
        ? `Approved crawled opportunity "${merged.title}" with edits (${editedKeys.join(', ')}) → opportunity ${created.id}`
        : `Approved crawled opportunity "${merged.title}" → opportunity ${created.id}`,
      previousData: { status: 'Pending' },
      newData: { status: 'Approved', approvedOpportunityId: created.id, editedFields: editedKeys },
    });

    return created;
  } catch (error) {
    console.error('Error approving crawled opportunity:', error);
    throw error;
  }
};

/**
 * Reject a crawled opportunity (kept for provenance, removed from the pending queue).
 */
export const rejectCrawledOpportunity = async (id: string, reviewerId?: string) => {
  try {
    await updateCrawledOpportunity(id, {
      status: 'Rejected',
      reviewedAt: { __type: 'Date', iso: new Date().toISOString() },
      ...(reviewerId ? { reviewedBy: reviewerId } : {}),
    });

    await logAdminActivity({
      action: 'crawledOpportunity.reject',
      targetType: 'crawledOpportunity',
      targetId: id,
      summary: `Rejected crawled opportunity ${id}`,
      previousData: { status: 'Pending' },
      newData: { status: 'Rejected' },
    });

    return { id, status: 'Rejected' };
  } catch (error) {
    console.error('Error rejecting crawled opportunity:', error);
    throw error;
  }
};

// ---- System analytics (admin overview) -------------------------------------

export interface SystemAnalytics {
  content: {
    totalOpportunities: number;
    claimedOpportunities: number;
    embeddings: number;
    embeddingCoveragePct: number; // % of opportunities with a stored embedding
  };
  crawler: {
    urlsTracked: number; // CrawlLog rows
    miniCalls: number; // pages extracted (one gpt-5.4-mini call each)
    nanoRejects: number; // pages the relevance gate rejected
    nanoCalls: number; // miniCalls + nanoRejects (pages that reached the nano gate)
    errors: number;
    lastCrawlAt: string | null;
    queuedLast7Days: number; // CrawledOpportunity created in the last 7 days
  };
  queues: {
    crawledPending: number;
    crawledApproved: number;
    crawledRejected: number;
    approvalRatePct: number; // approved / (approved + rejected)
    issuesPending: number;
    suggestionsPending: number;
  };
}

/**
 * One-shot health snapshot for the admin Analytics tab. Uses cheap Parse count queries
 * (count=1&limit=0) run in parallel — no full object fetches. Crawler LLM volumes are derived
 * from CrawlLog status/gateReason, so no provider dashboards are needed for a ballpark.
 */
export const getSystemAnalytics = async (): Promise<SystemAnalytics> => {
  // All the count queries run server-side with the master key (admin-verified) — see the
  // getSystemAnalytics cloud function in src/cloudCode/main.js.
  const Parse = await getParseInstance();
  return Parse.Cloud.run('getSystemAnalytics');
};

// App Configuration Management
export const getAppConfig = async (key: string): Promise<any> => {
  try {
    const response = await fetch(`${PARSE_SERVER_URL}/classes/AppConfig?where=${encodeURIComponent(JSON.stringify({ key }))}`, {
      method: 'GET',
      headers: {
        'X-Parse-Application-Id': PARSE_APP_ID,
        'X-Parse-REST-API-Key': PARSE_REST_API_KEY,
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get config: ${response.status}`);
    }

    const data = await response.json();
    const config = data.results && data.results[0];
    
    return config ? config.value : null;
  } catch (error) {
    console.error('Error getting app config:', error);
    return null;
  }
};

export const setAppConfig = async (key: string, value: any): Promise<boolean> => {
  try {
    // First, check if config exists
    const checkResponse = await fetch(`${PARSE_SERVER_URL}/classes/AppConfig?where=${encodeURIComponent(JSON.stringify({ key }))}`, {
      method: 'GET',
      headers: {
        'X-Parse-Application-Id': PARSE_APP_ID,
        'X-Parse-REST-API-Key': PARSE_REST_API_KEY,
      }
    });

    const checkData = await checkResponse.json();
    const existingConfig = checkData.results && checkData.results[0];

    if (existingConfig) {
      // Update existing config
      const response = await fetch(`${PARSE_SERVER_URL}/classes/AppConfig/${existingConfig.objectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Parse-Application-Id': PARSE_APP_ID,
          'X-Parse-REST-API-Key': PARSE_REST_API_KEY,
        },
        body: JSON.stringify({
          value,
          updatedAt: { __type: 'Date', iso: new Date().toISOString() }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update config: ${response.status}`);
      }
    } else {
      // Create new config
      const response = await fetch(`${PARSE_SERVER_URL}/classes/AppConfig`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Parse-Application-Id': PARSE_APP_ID,
          'X-Parse-REST-API-Key': PARSE_REST_API_KEY,
        },
        body: JSON.stringify({
          key,
          value,
          updatedAt: { __type: 'Date', iso: new Date().toISOString() }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create config: ${response.status}`);
      }
    }
    
    console.log(`App config '${key}' updated successfully`);
    return true;
  } catch (error) {
    console.error('Error setting app config:', error);
    return false;
  }
};

// Featured Opportunity Management
export const getFeaturedOpportunityId = async (): Promise<string | null> => {
  try {
    return await getAppConfig('featuredOpportunityId');
  } catch (error) {
    console.error('Error getting featured opportunity ID:', error);
    return null;
  }
};

export const setFeaturedOpportunityId = async (opportunityId: string | null): Promise<boolean> => {
  try {
    const previousFeaturedId = await getFeaturedOpportunityId();
    // Validate that the opportunity exists if an ID is provided
    if (opportunityId) {
      const exists = await opportunityExists(opportunityId);
      if (!exists) {
        console.error('Cannot set featured opportunity: Opportunity does not exist');
        return false;
      }
    }
    
    const success = await setAppConfig('featuredOpportunityId', opportunityId);
    if (success) {
      console.log(`Featured opportunity set to: ${opportunityId || 'none'}`);
      await logAdminActivity({
        action: 'featured.set',
        targetType: 'featured',
        targetId: opportunityId,
        summary: `Set featured opportunity to ${opportunityId || 'none'}`,
        previousData: { featuredOpportunityId: previousFeaturedId || null },
        newData: { featuredOpportunityId: opportunityId || null },
      });
    }
    return success;
  } catch (error) {
    console.error('Error setting featured opportunity ID:', error);
    return false;
  }
};

export const getFeaturedOpportunity = async () => {
  try {
    const featuredId = await getFeaturedOpportunityId();
    if (!featuredId) {
      return null;
    }
    
    return await getOpportunityById(featuredId);
  } catch (error) {
    console.error('Error getting featured opportunity:', error);
    return null;
  }
};

// Admin user management
export const getAdminUsers = async (): Promise<any[]> => {
  try {
    const Parse = await getParseInstance();
    const results = await Parse.Cloud.run('getAdminUsers');
    return results || [];
  } catch (error) {
    console.error('Error fetching admin users:', error);
    return [];
  }
};

export const updateAdminUserType = async (userId: string, newUserType: 'admin' | 'super_admin', previousUserType?: string) => {
  try {
    // Role change applied server-side with the master key, gated to super admins.
    const Parse = await getParseInstance();
    const { previousUserType: serverPrevious } = await Parse.Cloud.run('updateAdminUserType', {
      userId,
      newUserType,
    });

    await logAdminActivity({
      action: 'admin.updateUserType',
      targetType: 'user',
      targetId: userId,
      summary: `Updated admin role to ${newUserType}`,
      previousData: { userType: previousUserType || serverPrevious || null },
      newData: { userType: newUserType },
    });

    return true;
  } catch (error) {
    console.error('Error updating admin user type:', error);
    return false;
  }
};

// Admin activity feed
export const getAdminActivity = async (params?: {
  limit?: number;
  actorId?: string | null;
  targetType?: string | null;
}) => {
  try {
    const Parse = await getParseInstance();
    const results = await Parse.Cloud.run('getAdminActivity', {
      limit: params?.limit || 100,
      actorId: params?.actorId || undefined,
      targetType: params?.targetType || undefined,
    });
    return results || [];
  } catch (error) {
    console.error('Error fetching admin activity:', error);
    return [];
  }
};

// Opportunity deletion requests
export const requestOpportunityDeletion = async (data: {
  opportunityId: string;
  reason?: string;
}) => {
  // The cloud function verifies the caller is an admin, confirms the opportunity still exists and
  // that there is no pending duplicate, then creates the DeletionRequest with the master key
  // (requestedBy is derived from the session, not the client).
  const Parse = await getParseInstance();
  await Parse.Cloud.run('requestOpportunityDeletion', {
    opportunityId: data.opportunityId,
    reason: data.reason || null,
  });

  await logAdminActivity({
    action: 'opportunity.deleteRequest',
    targetType: 'opportunity',
    targetId: data.opportunityId,
    summary: 'Requested opportunity deletion',
    previousData: null,
    newData: { reason: data.reason || null },
  });
};

export const getDeletionRequests = async (status?: 'Pending' | 'Approved' | 'Rejected') => {
  const Parse = await getParseInstance();
  const results = await Parse.Cloud.run('getDeletionRequests', status ? { status } : {});
  return results || [];
};

export const updateDeletionRequestStatus = async (
  requestId: string,
  status: 'Approved' | 'Rejected',
  _reviewerId?: string,
) => {
  // Super-admin only; reviewedBy is set from the session server-side, and processed-request
  // cleanup runs inside the cloud function.
  const Parse = await getParseInstance();
  await Parse.Cloud.run('updateDeletionRequestStatus', { requestId, status });
};

export const approveDeletionRequest = async (request: any) => {
  const actor = await getCurrentAdminActor();
  if (!actor) throw new Error('Admin access required');
  if (actor.userType !== 'super_admin') {
    throw new Error('Only super admins can approve deletion requests');
  }

  const requestId = request?.objectId || request?.id;
  const opportunityId =
    request?.opportunity?.objectId ||
    request?.opportunity?.id;
  
  if (!requestId || !opportunityId) {
    throw new Error('Invalid deletion request - missing IDs');
  }

  // Check if request is still pending
  if (request.status && request.status !== 'Pending') {
    throw new Error(`Request already ${request.status.toLowerCase()}`);
  }

  // Check if opportunity still exists
  const exists = await opportunityExists(opportunityId);
  if (!exists) {
    // Update request to reflect opportunity is gone
    await updateDeletionRequestStatus(requestId, 'Approved', actor.id);
    throw new Error('Opportunity was already deleted');
  }

  // Update request status first
  await updateDeletionRequestStatus(requestId, 'Approved', actor.id);
  
  // Then delete the opportunity
  await deleteOpportunity(opportunityId);

  await logAdminActivity({
    action: 'opportunity.deleteApproved',
    targetType: 'opportunity',
    targetId: opportunityId,
    summary: `Approved deletion of "${request.opportunity?.title || 'Unknown'}"`,
    previousData: { requestId, reason: request.reason },
    newData: null,
  });
};

export const rejectDeletionRequest = async (request: any) => {
  const actor = await getCurrentAdminActor();
  if (!actor) throw new Error('Admin access required');
  if (actor.userType !== 'super_admin') {
    throw new Error('Only super admins can reject deletion requests');
  }

  const requestId = request?.objectId || request?.id;
  const opportunityId =
    request?.opportunity?.objectId ||
    request?.opportunity?.id;
  
  if (!requestId) {
    throw new Error('Invalid deletion request - missing ID');
  }

  // Check if request is still pending
  if (request.status && request.status !== 'Pending') {
    throw new Error(`Request already ${request.status.toLowerCase()}`);
  }

  await updateDeletionRequestStatus(requestId, 'Rejected', actor.id);

  await logAdminActivity({
    action: 'opportunity.deleteRejected',
    targetType: 'opportunity',
    targetId: opportunityId || null,
    summary: `Rejected deletion of "${request.opportunity?.title || 'Unknown'}"`,
    previousData: { requestId, reason: request.reason },
    newData: null,
  });
};
export const revertAdminActivity = async (activity: any) => {
  const action = activity?.action;
  const targetId = activity?.targetId;
  const previousData = (() => {
    const raw = activity?.previousData;
    if (!raw) return {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    return raw;
  })();

  if (!action) {
    throw new Error('Invalid activity record - missing action');
  }

  // Prevent reverting restore operations to avoid circular loops
  if (action === 'opportunity.restore' || action === 'admin.revert') {
    throw new Error('Cannot revert a restore or revert operation');
  }

  switch (action) {
    case 'opportunity.update': {
      if (!targetId) throw new Error('Missing opportunity target ID');
      if (!previousData || Object.keys(previousData).length === 0) {
        throw new Error('Cannot revert - no previous data available');
      }
      
      // Check if opportunity still exists
      const exists = await opportunityExists(targetId);
      if (!exists) {
        throw new Error('Cannot revert - opportunity no longer exists');
      }
      
      try {
        await updateOpportunity(targetId, previousData);
      } catch (error) {
        console.warn('Standard update failed, trying with master key:', error);
        await updateOpportunityWithMasterKey(targetId, previousData);
      }
      break;
    }
    case 'opportunity.create': {
      if (!targetId) throw new Error('Missing opportunity target ID');
      
      // Check if opportunity still exists
      const exists = await opportunityExists(targetId);
      if (!exists) {
        throw new Error('Cannot revert - opportunity was already deleted');
      }
      
      await deleteOpportunity(targetId);
      break;
    }
    case 'opportunity.delete': {
      if (!previousData || Object.keys(previousData).length === 0) {
        throw new Error('Cannot restore - no snapshot data available');
      }
      if (!previousData.title || !previousData.organization) {
        throw new Error('Cannot restore - incomplete snapshot data');
      }
      
      const recreated = await createOpportunity(previousData);
      await logAdminActivity({
        action: 'opportunity.restore',
        targetType: 'opportunity',
        targetId: recreated?.id || null,
        summary: `Restored opportunity "${previousData.title || 'Unknown'}"`,
        previousData: null,
        newData: serializeOpportunityForAudit(recreated),
        metadata: { restoredFromActivityId: activity.objectId || activity.id },
      });
      break;
    }
    case 'issue.updateStatus': {
      if (!targetId) throw new Error('Missing issue target ID');
      if (!previousData.status) throw new Error('Missing previous status');
      await updateIssueReportStatus(targetId, previousData.status, previousData.adminNotes);
      break;
    }
    case 'suggestion.updateStatus': {
      if (!targetId) throw new Error('Missing suggestion target ID');
      if (!previousData.status) throw new Error('Missing previous status');
      await updateOpportunitySuggestionStatus(targetId, previousData.status, previousData.adminNotes);
      break;
    }
    case 'featured.set': {
      await setFeaturedOpportunityId(previousData.featuredOpportunityId || null);
      break;
    }
    case 'admin.updateUserType': {
      if (!targetId) throw new Error('Missing user target ID');
      if (!previousData.userType) throw new Error('Missing previous role');
      await updateAdminUserType(targetId, previousData.userType);
      break;
    }
    default:
      throw new Error(`Revert not supported for action: ${action}`);
  }

  await logAdminActivity({
    action: 'admin.revert',
    targetType: activity?.targetType || 'unknown',
    targetId: targetId || null,
    summary: `Reverted: ${activity?.summary || action}`,
    previousData: null,
    newData: null,
    metadata: { revertedActivityId: activity.objectId || activity.id, revertedAction: action },
  });
};

// Organization-specific opportunity management
export const createOrganizationOpportunity = async (data: {
  title: string;
  description: string;
  type: string;
  organization: string;
  startDate?: Date | string;
  endDate?: Date | string;
  applicationOpenDate?: Date | string;
  applicationCloseDate?: Date | string;
  deadline?: Date | string;
  location: string;
  eligibility?: string;
  website?: string;
  tags?: string[];
  isClaimed?: boolean;
  isYearlyRecurring?: boolean;
  gradeLevel?: string[];
  status: string;
  cost?: string;
  createdBy?: string; // Organization user ID
}) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const opportunity = new Opportunity();
    
    // Set all the opportunity fields
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        // Convert string dates to actual Date objects
        if (['startDate', 'endDate', 'deadline', 'applicationOpenDate', 'applicationCloseDate'].includes(key) && typeof value === 'string') {
          try {
            opportunity.set(key, new Date(value));
          } catch (e) {
            console.error(`Invalid date for ${key}:`, value);
            // Don't set this field if invalid
          }
        } else {
          opportunity.set(key, value);
        }
      }
    });
    
    // Add timestamps and organization-specific fields
    opportunity.set('createdAt', new Date());
    opportunity.set('updatedAt', new Date());
    opportunity.set('isClaimed', true); // Organization-created opportunities are automatically claimed
    opportunity.set('createdBy', data.createdBy || Parse.User.current()?.id);
    
    // Save the opportunity
    const result = await opportunity.save();
    console.log('Organization opportunity created with objectId:', result.id);
    return result;
  } catch (error) {
    console.error('Error creating organization opportunity:', error);
    throw error;
  }
};

export const getOrganizationOpportunities = async (organizationUserId: string) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);
    
    // Filter by organization user
    query.equalTo('createdBy', organizationUserId);
    query.descending('createdAt');
    query.limit(100);
    
    // Note: Organization dashboards can see all their opportunities, including hidden ones
    // This is different from getOpportunities() which filters out hidden opportunities for public search
    
    const results = await query.find();
    return results;
  } catch (error) {
    console.error('Error fetching organization opportunities:', error);
    throw error;
  }
};

export const updateOrganizationOpportunity = async (id: string, data: any, organizationUserId: string) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);
    
    // Ensure the opportunity belongs to the organization
    query.equalTo('createdBy', organizationUserId);
    const opportunity = await query.get(id);
    
    // Update fields from data
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        // Handle date conversions
        if (['startDate', 'endDate', 'deadline', 'applicationOpenDate', 'applicationCloseDate'].includes(key)) {
          if (value === null) {
            // Explicitly clear the date field
            opportunity.unset(key);
          } else if (typeof value === 'string') {
            try {
              opportunity.set(key, new Date(value));
            } catch (e) {
              console.error(`Invalid date for ${key}:`, value);
              // Don't set this field if invalid
            }
          } else if (value instanceof Date) {
            opportunity.set(key, value);
          }
        } else {
          opportunity.set(key, value);
        }
      }
    });
    
    // Always update the updatedAt timestamp
    opportunity.set('updatedAt', new Date());
    
    // Save the updated opportunity
    const result = await opportunity.save(null);
    console.log(`Organization opportunity with ID ${id} updated successfully`);
    return result;
  } catch (error) {
    console.error('Error updating organization opportunity:', error);
    throw error;
  }
};

export const deleteOrganizationOpportunity = async (id: string, organizationUserId: string) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);
    
    // Ensure the opportunity belongs to the organization
    query.equalTo('createdBy', organizationUserId);
    const opportunity = await query.get(id);
    
    // Delete the opportunity
    await opportunity.destroy();
    console.log(`Organization opportunity with ID ${id} deleted successfully`);
    return true;
  } catch (error) {
    console.error('Error deleting organization opportunity:', error);
    throw error;
  }
};

export const getOrganizationStats = async (organizationUserId: string) => {
  try {
    const Parse = await getParseInstance();
    
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);
    
    // Filter by organization user
    query.equalTo('createdBy', organizationUserId);
    
    // Get all opportunities for this organization
    const opportunities = await query.find();
    
    // Calculate stats
    const totalOpportunities = opportunities.length;
    const activeOpportunities = opportunities.filter((opp: any) => {
      const status = opp.get('status');
      return status === 'Open' || status === 'Coming Soon';
    }).length;
    
    // For now, impressions and total views are coming soon
    const impressions = 0; // TODO: Implement view tracking
    const totalViews = 0; // TODO: Implement view tracking
    
    return {
      totalOpportunities,
      activeOpportunities,
      impressions,
      totalViews
    };
  } catch (error) {
    console.error('Error fetching organization stats:', error);
    return {
      totalOpportunities: 0,
      activeOpportunities: 0,
      impressions: 0,
      totalViews: 0
    };
  }
};

// Initialize embeddings for AI search (admin function)
export const initializeAIEmbeddings = async (forceRegenerate: boolean = false): Promise<{ success: boolean; message: string }> => {
  try {
    // Get ALL opportunities, not just the default 100
    const opportunities = await getOpportunities(1000);

    if (opportunities.length === 0) {
      return { success: false, message: 'No opportunities found to embed' };
    }

    // Import the embedding function
    const { initializeEmbeddings } = await import('./aiSearchService');

    // Parse opportunities to proper format
    const parsedOpportunities = opportunities.map((opp: any) => {
      if (opp.attributes) {
        const id = opp.id;
        const attributes = { ...opp.attributes };
        return { id, ...attributes };
      }
      return opp;
    });

    await initializeEmbeddings(parsedOpportunities, forceRegenerate);

    return {
      success: true,
      message: `Successfully ${forceRegenerate ? 'regenerated' : 'initialized'} embeddings for ${parsedOpportunities.length} opportunities`
    };
  } catch (error) {
    console.error('Error initializing AI embeddings:', error);
    return {
      success: false,
      message: `Failed to ${forceRegenerate ? 'regenerate' : 'initialize'} embeddings: ${error instanceof Error ? error.message : String(error)}`
    };
  }
};

// ============================================================================
// SCHOOL PARTNERSHIP FUNCTIONS
// ============================================================================

// ----------------------------------------------------------------------------
// School Management Functions
// ----------------------------------------------------------------------------

export const getSchoolBySlug = async (slug: string): Promise<Parse.Object | null> => {
  try {
    const Parse = await getParseInstance();
    const School = Parse.Object.extend('School');
    const query = new Parse.Query(School);

    query.equalTo('slug', slug);
    query.equalTo('isActive', true);

    const school = await query.first();
    return school || null;
  } catch (error) {
    console.error('Error fetching school by slug:', error);
    throw error;
  }
};

export const getAllSchools = async (): Promise<Parse.Object[]> => {
  try {
    const Parse = await getParseInstance();
    const School = Parse.Object.extend('School');
    const query = new Parse.Query(School);

    query.equalTo('isActive', true);
    query.ascending('name');

    const schools = await query.find();
    return schools;
  } catch (error) {
    console.error('Error fetching all schools:', error);
    throw error;
  }
};

export const createSchool = async (data: {
  name: string;
  slug: string;
  district: string;
  address: string;
  contactEmail: string;
  website?: string;
  description?: string;
}): Promise<Parse.Object> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error('Must be logged in to create school');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'super_admin') {
      throw new Error('Only super admins can create schools');
    }

    const School = Parse.Object.extend('School');
    const school = new School();

    school.set('name', data.name);
    school.set('slug', data.slug);
    school.set('district', data.district);
    school.set('address', data.address);
    school.set('contactEmail', data.contactEmail);
    school.set('isActive', true);

    if (data.website) school.set('website', data.website);
    if (data.description) school.set('description', data.description);

    const result = await school.save();
    console.log(`School created successfully with slug: ${data.slug}`);
    return result;
  } catch (error) {
    console.error('Error creating school:', error);
    throw error;
  }
};

export const updateSchool = async (schoolId: string, data: Partial<{
  name: string;
  district: string;
  address: string;
  contactEmail: string;
  website: string;
  description: string;
  isActive: boolean;
}>): Promise<Parse.Object> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error('Must be logged in to update school');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'super_admin' && userType !== 'school_admin') {
      throw new Error('Only admins can update schools');
    }

    const School = Parse.Object.extend('School');
    const query = new Parse.Query(School);
    const school = await query.get(schoolId);

    // School admins can only update their own school
    if (userType === 'school_admin') {
      const userSchool = currentUser.get('school');
      if (!userSchool || userSchool.id !== schoolId) {
        throw new Error('School admins can only update their own school');
      }
    }

    if (data.name !== undefined) school.set('name', data.name);
    if (data.district !== undefined) school.set('district', data.district);
    if (data.address !== undefined) school.set('address', data.address);
    if (data.contactEmail !== undefined) school.set('contactEmail', data.contactEmail);
    if (data.website !== undefined) school.set('website', data.website);
    if (data.description !== undefined) school.set('description', data.description);
    if (data.isActive !== undefined && userType === 'super_admin') {
      school.set('isActive', data.isActive);
    }

    const result = await school.save();
    console.log(`School ${schoolId} updated successfully`);
    return result;
  } catch (error) {
    console.error('Error updating school:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------------
// School Admin Functions
// ----------------------------------------------------------------------------

export const getSchoolOpportunities = async (schoolId: string): Promise<Array<{
  schoolOpportunity: Parse.Object;
  opportunity: Parse.Object;
  teacherCount: number;
  teachers: Array<{ name: string; roomNumber?: string }>;
}>> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error('Must be logged in');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'school_admin' && userType !== 'teacher' && userType !== 'super_admin') {
      throw new Error('Access denied');
    }

    // Verify user belongs to the requested school
    const userSchool = currentUser.get('school');
    if (userType !== 'super_admin' && (!userSchool || userSchool.id !== schoolId)) {
      throw new Error('Can only view your own school opportunities');
    }

    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const query = new Parse.Query(SchoolOpportunity);

    const School = Parse.Object.extend('School');
    const schoolPointer = School.createWithoutData(schoolId);
    query.equalTo('school', schoolPointer);
    query.include('opportunity');
    query.descending('createdAt');

    const schoolOpportunities = await query.find();

    // Batch fetch all teacher signups
    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const signupQuery = new Parse.Query(TeacherSignup);
    signupQuery.containedIn('schoolOpportunity', schoolOpportunities);
    signupQuery.equalTo('isActive', true);
    signupQuery.include('teacher');
    const allSignups = await signupQuery.find();

    // Group by school opportunity ID
    const signupMap: Record<string, Array<{ name: string; roomNumber?: string }>> = {};
    for (const signup of allSignups) {
      const soId = signup.get('schoolOpportunity')?.id;
      if (!soId) continue;
      const teacher = signup.get('teacher');
      if (!teacher) continue;
      if (!signupMap[soId]) signupMap[soId] = [];
      signupMap[soId].push({
        name: teacher.get('displayName') || teacher.get('username') || 'Teacher',
        roomNumber: signup.get('roomNumber'),
      });
    }

    const results = schoolOpportunities.map((so: Parse.Object) => {
      const teachers = signupMap[so.id] || [];
      return {
        schoolOpportunity: so,
        opportunity: so.get('opportunity'),
        teacherCount: teachers.length,
        teachers,
      };
    });

    return results;
  } catch (error) {
    console.error('Error fetching school opportunities:', error);
    throw error;
  }
};

export const addOpportunityToSchool = async (
  schoolId: string,
  opportunityId: string,
  userId: string
): Promise<Parse.Object> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== userId) {
      throw new Error('Unauthorized');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'school_admin') {
      throw new Error('Only school admins can add opportunities');
    }

    const userSchool = currentUser.get('school');
    if (!userSchool || userSchool.id !== schoolId) {
      throw new Error('School admins can only add opportunities to their own school');
    }

    // Check if already added
    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const checkQuery = new Parse.Query(SchoolOpportunity);
    const School = Parse.Object.extend('School');
    const Opportunity = Parse.Object.extend('Opportunity');

    checkQuery.equalTo('school', School.createWithoutData(schoolId));
    checkQuery.equalTo('opportunity', Opportunity.createWithoutData(opportunityId));
    const existing = await checkQuery.first();

    if (existing) {
      throw new Error('Opportunity already added to this school');
    }

    // Create the school opportunity
    const schoolOpportunity = new SchoolOpportunity();
    schoolOpportunity.set('school', School.createWithoutData(schoolId));
    schoolOpportunity.set('opportunity', Opportunity.createWithoutData(opportunityId));
    schoolOpportunity.set('addedBy', userId);
    schoolOpportunity.set('isVisible', true);

    const result = await schoolOpportunity.save();
    console.log(`Opportunity ${opportunityId} added to school ${schoolId}`);
    return result;
  } catch (error) {
    console.error('Error adding opportunity to school:', error);
    throw error;
  }
};

export const removeOpportunityFromSchool = async (
  schoolOpportunityId: string,
  userId: string
): Promise<boolean> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== userId) {
      throw new Error('Unauthorized');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'school_admin') {
      throw new Error('Only school admins can remove opportunities');
    }

    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const query = new Parse.Query(SchoolOpportunity);
    query.include('school');

    let schoolOpportunity;
    try {
      schoolOpportunity = await query.get(schoolOpportunityId);
    } catch {
      throw new Error('School opportunity not found');
    }
    const school = schoolOpportunity.get('school');
    if (!school) {
      throw new Error('School opportunity has no linked school');
    }

    // Verify admin owns this school
    const userSchool = currentUser.get('school');
    if (!userSchool || userSchool.id !== school.id) {
      throw new Error('Cannot remove opportunities from other schools');
    }

    // Delete associated teacher signups first
    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const signupQuery = new Parse.Query(TeacherSignup);
    signupQuery.equalTo('schoolOpportunity', schoolOpportunity);
    const signups = await signupQuery.find();

    if (signups.length > 0) {
      await Parse.Object.destroyAll(signups);
    }

    // Delete the school opportunity
    await schoolOpportunity.destroy();
    console.log(`School opportunity ${schoolOpportunityId} removed`);
    return true;
  } catch (error) {
    console.error('Error removing opportunity from school:', error);
    throw error;
  }
};

export const createSchoolOpportunity = async (
  schoolId: string,
  opportunityData: any,
  userId: string
): Promise<{ opportunity: Parse.Object; schoolOpportunity: Parse.Object }> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== userId) {
      throw new Error('Unauthorized');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'school_admin') {
      throw new Error('Only school admins can create opportunities');
    }

    const userSchool = currentUser.get('school');
    if (!userSchool || userSchool.id !== schoolId) {
      throw new Error('School admins can only create opportunities for their own school');
    }

    // Create the opportunity (available globally)
    const opportunity = await createOpportunity(opportunityData);

    // Link it to the school
    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const schoolOpportunity = new SchoolOpportunity();

    const School = Parse.Object.extend('School');
    schoolOpportunity.set('school', School.createWithoutData(schoolId));
    schoolOpportunity.set('opportunity', opportunity);
    schoolOpportunity.set('addedBy', userId);
    schoolOpportunity.set('isVisible', true);

    await schoolOpportunity.save();

    console.log(`New opportunity created and added to school ${schoolId}`);
    return { opportunity, schoolOpportunity };
  } catch (error) {
    console.error('Error creating school opportunity:', error);
    throw error;
  }
};

// Create opportunity with school linking (for both teachers and admins)
// schoolOnly = true means opportunity is hidden from global search
export const createOpportunityForSchool = async (
  schoolId: string,
  opportunityData: {
    title: string;
    description: string;
    type: string;
    organization: string;
    startDate?: string;
    endDate?: string;
    applicationOpenDate?: string;
    applicationCloseDate?: string;
    deadline?: string;
    location: string;
    eligibility?: string;
    website?: string;
    tags?: string[];
    gradeLevel?: string[];
    cost?: string;
  },
  userId: string,
  schoolOnly: boolean = false
): Promise<{ opportunity: Parse.Object; schoolOpportunity: Parse.Object }> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== userId) {
      throw new Error('Unauthorized');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'school_admin' && userType !== 'teacher') {
      throw new Error('Only school admins and teachers can create opportunities');
    }

    const userSchool = currentUser.get('school');
    if (!userSchool || userSchool.id !== schoolId) {
      throw new Error('Can only create opportunities for your own school');
    }

    // Create the opportunity with isHiddenFromSearch based on schoolOnly flag
    const opportunity = await createOpportunity({
      ...opportunityData,
      status: 'active',
      isHiddenFromSearch: schoolOnly, // Hide from global search if school-only
    } as any);

    // Link it to the school
    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const schoolOpportunity = new SchoolOpportunity();

    const School = Parse.Object.extend('School');
    schoolOpportunity.set('school', School.createWithoutData(schoolId));
    schoolOpportunity.set('opportunity', opportunity);
    schoolOpportunity.set('addedBy', userId);
    schoolOpportunity.set('addedByType', userType);
    schoolOpportunity.set('isVisible', true);
    schoolOpportunity.set('isSchoolOnly', schoolOnly);

    await schoolOpportunity.save();

    console.log(`New opportunity created by ${userType} and added to school ${schoolId} (schoolOnly: ${schoolOnly})`);
    return { opportunity, schoolOpportunity };
  } catch (error) {
    console.error('Error creating opportunity for school:', error);
    throw error;
  }
};

export const searchOpportunitiesForSchool = async (
  searchTerm: string,
  filters?: {
    type?: string[];
    gradeLevel?: string[];
  }
): Promise<Parse.Object[]> => {
  try {
    const Parse = await getParseInstance();
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(Opportunity);

    if (searchTerm) {
      const titleQuery = new Parse.Query(Opportunity);
      titleQuery.matches('title', new RegExp(searchTerm, 'i'));

      const orgQuery = new Parse.Query(Opportunity);
      orgQuery.matches('organization', new RegExp(searchTerm, 'i'));

      const descQuery = new Parse.Query(Opportunity);
      descQuery.matches('description', new RegExp(searchTerm, 'i'));

      query._orQuery([titleQuery, orgQuery, descQuery]);
    }

    if (filters?.type && filters.type.length > 0) {
      query.containedIn('type', filters.type);
    }

    if (filters?.gradeLevel && filters.gradeLevel.length > 0) {
      query.containsAll('gradeLevel', filters.gradeLevel);
    }

    query.descending('createdAt');
    query.limit(50);

    const opportunities = await query.find();
    return opportunities;
  } catch (error) {
    console.error('Error searching opportunities:', error);
    throw error;
  }
};

export const getSchoolAdminStats = async (schoolId: string): Promise<{
  totalOpportunities: number;
  activeOpportunities: number;
  totalTeacherSignups: number;
}> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error('Must be logged in');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'school_admin' && userType !== 'teacher' && userType !== 'super_admin') {
      throw new Error('Access denied');
    }

    // Verify user belongs to the requested school
    const userSchool = currentUser.get('school');
    if (userType !== 'super_admin' && (!userSchool || userSchool.id !== schoolId)) {
      throw new Error('Can only view your own school stats');
    }

    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const School = Parse.Object.extend('School');
    const schoolPointer = School.createWithoutData(schoolId);

    // Total opportunities
    const totalQuery = new Parse.Query(SchoolOpportunity);
    totalQuery.equalTo('school', schoolPointer);
    const totalOpportunities = await totalQuery.count();

    // Active opportunities (visible)
    const activeQuery = new Parse.Query(SchoolOpportunity);
    activeQuery.equalTo('school', schoolPointer);
    activeQuery.equalTo('isVisible', true);
    const activeOpportunities = await activeQuery.count();

    // Total teacher signups
    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const signupQuery = new Parse.Query(TeacherSignup);
    signupQuery.equalTo('school', schoolPointer);
    signupQuery.equalTo('isActive', true);
    const totalTeacherSignups = await signupQuery.count();

    return {
      totalOpportunities,
      activeOpportunities,
      totalTeacherSignups
    };
  } catch (error) {
    console.error('Error fetching school admin stats:', error);
    return {
      totalOpportunities: 0,
      activeOpportunities: 0,
      totalTeacherSignups: 0
    };
  }
};

export const updateSchoolOpportunityVisibility = async (
  schoolOpportunityId: string,
  isVisible: boolean,
  userId: string
): Promise<Parse.Object> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== userId) {
      throw new Error('Unauthorized');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'school_admin') {
      throw new Error('Only school admins can update opportunity visibility');
    }

    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const query = new Parse.Query(SchoolOpportunity);
    query.include('school');

    let schoolOpportunity;
    try {
      schoolOpportunity = await query.get(schoolOpportunityId);
    } catch {
      throw new Error('School opportunity not found');
    }
    const school = schoolOpportunity.get('school');
    if (!school) {
      throw new Error('School opportunity has no linked school');
    }

    // Verify admin owns this school
    const userSchool = currentUser.get('school');
    if (!userSchool || userSchool.id !== school.id) {
      throw new Error('Cannot update opportunities from other schools');
    }

    schoolOpportunity.set('isVisible', isVisible);
    const result = await schoolOpportunity.save();

    console.log(`School opportunity ${schoolOpportunityId} visibility set to ${isVisible}`);
    return result;
  } catch (error) {
    console.error('Error updating opportunity visibility:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------------
// Teacher Functions
// ----------------------------------------------------------------------------

export const getTeacherSchoolOpportunities = async (schoolId: string): Promise<Array<{
  schoolOpportunity: Parse.Object;
  opportunity: Parse.Object;
  teacherSignups: Parse.Object[];
  userHasSignedUp: boolean;
}>> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser) {
      throw new Error('Must be logged in');
    }

    const userType = currentUser.get('userType');
    if (userType === 'teacher') {
      const userSchool = currentUser.get('school');
      if (!userSchool || userSchool.id !== schoolId) {
        throw new Error('Teachers can only view their own school opportunities');
      }
    }

    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const query = new Parse.Query(SchoolOpportunity);

    const School = Parse.Object.extend('School');
    const schoolPointer = School.createWithoutData(schoolId);
    query.equalTo('school', schoolPointer);
    query.equalTo('isVisible', true);
    query.include('opportunity');
    query.descending('createdAt');

    const schoolOpportunities = await query.find();

    // Get teacher signups for each opportunity
    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const results = await Promise.all(schoolOpportunities.map(async (so: Parse.Object) => {
      const signupQuery = new Parse.Query(TeacherSignup);
      signupQuery.equalTo('schoolOpportunity', so);
      signupQuery.equalTo('isActive', true);
      signupQuery.include('teacher');
      const teacherSignups = await signupQuery.find();

      // Check if current user has signed up (safely handle undefined teacher)
      const userHasSignedUp = teacherSignups.some((signup: Parse.Object) => {
        const teacher = signup.get('teacher');
        return teacher && teacher.id === currentUser.id;
      });

      return {
        schoolOpportunity: so,
        opportunity: so.get('opportunity'),
        teacherSignups,
        userHasSignedUp
      };
    }));

    return results;
  } catch (error) {
    console.error('Error fetching teacher school opportunities:', error);
    throw error;
  }
};

export const signUpForOpportunity = async (
  teacherId: string,
  schoolOpportunityId: string,
  data: {
    availability?: string;
    roomNumber?: string;
    notes?: string;
  }
): Promise<Parse.Object> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== teacherId) {
      throw new Error('Unauthorized');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'teacher') {
      throw new Error('Only teachers can sign up for opportunities');
    }

    // Check if already signed up
    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const checkQuery = new Parse.Query(TeacherSignup);
    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');

    checkQuery.equalTo('teacher', currentUser);
    checkQuery.equalTo('schoolOpportunity', SchoolOpportunity.createWithoutData(schoolOpportunityId));
    checkQuery.equalTo('isActive', true);
    const existing = await checkQuery.first();

    if (existing) {
      throw new Error('Already signed up for this opportunity');
    }

    // Get school opportunity to extract school and opportunity
    const soQuery = new Parse.Query(SchoolOpportunity);
    soQuery.include('school');
    soQuery.include('opportunity');
    let schoolOpportunity;
    try {
      schoolOpportunity = await soQuery.get(schoolOpportunityId);
    } catch {
      throw new Error('School opportunity not found');
    }

    // Verify teacher belongs to this school
    const userSchool = currentUser.get('school');
    const opportunitySchool = schoolOpportunity.get('school');
    if (!opportunitySchool) {
      throw new Error('School opportunity has no linked school');
    }
    if (!userSchool || userSchool.id !== opportunitySchool.id) {
      throw new Error('Can only sign up for opportunities at your school');
    }

    // Create the signup
    const signup = new TeacherSignup();
    signup.set('teacher', currentUser);
    signup.set('schoolOpportunity', schoolOpportunity);
    signup.set('school', opportunitySchool);
    signup.set('opportunity', schoolOpportunity.get('opportunity'));
    signup.set('isActive', true);

    if (data.availability) signup.set('availability', data.availability);
    if (data.roomNumber) signup.set('roomNumber', data.roomNumber);
    if (data.notes) signup.set('notes', data.notes);

    const result = await signup.save();
    console.log(`Teacher ${teacherId} signed up for opportunity ${schoolOpportunityId}`);
    return result;
  } catch (error) {
    console.error('Error signing up for opportunity:', error);
    throw error;
  }
};

export const updateTeacherSignup = async (
  signupId: string,
  teacherId: string,
  data: {
    availability?: string;
    roomNumber?: string;
    notes?: string;
  }
): Promise<Parse.Object> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== teacherId) {
      throw new Error('Unauthorized');
    }

    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const query = new Parse.Query(TeacherSignup);
    query.include('teacher');

    let signup;
    try {
      signup = await query.get(signupId);
    } catch {
      throw new Error('Signup not found');
    }

    // Verify ownership
    const teacher = signup.get('teacher');
    if (!teacher || teacher.id !== currentUser.id) {
      throw new Error('Can only update your own signups');
    }

    if (data.availability !== undefined) signup.set('availability', data.availability);
    if (data.roomNumber !== undefined) signup.set('roomNumber', data.roomNumber);
    if (data.notes !== undefined) signup.set('notes', data.notes);

    const result = await signup.save();
    console.log(`Teacher signup ${signupId} updated`);
    return result;
  } catch (error) {
    console.error('Error updating teacher signup:', error);
    throw error;
  }
};

export const removeTeacherSignup = async (
  signupId: string,
  teacherId: string
): Promise<boolean> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== teacherId) {
      throw new Error('Unauthorized');
    }

    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const query = new Parse.Query(TeacherSignup);
    query.include('teacher');

    let signup;
    try {
      signup = await query.get(signupId);
    } catch {
      throw new Error('Signup not found');
    }

    // Verify ownership
    const teacher = signup.get('teacher');
    if (!teacher || teacher.id !== currentUser.id) {
      throw new Error('Can only remove your own signups');
    }

    // Soft delete by setting isActive to false
    signup.set('isActive', false);
    await signup.save();

    console.log(`Teacher signup ${signupId} removed`);
    return true;
  } catch (error) {
    console.error('Error removing teacher signup:', error);
    throw error;
  }
};

export const getTeacherSignups = async (teacherId: string): Promise<Parse.Object[]> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== teacherId) {
      throw new Error('Unauthorized');
    }

    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const query = new Parse.Query(TeacherSignup);

    query.equalTo('teacher', currentUser);
    query.equalTo('isActive', true);
    query.include('opportunity');
    query.include('schoolOpportunity');
    query.descending('createdAt');

    const signups = await query.find();
    return signups;
  } catch (error) {
    console.error('Error fetching teacher signups:', error);
    throw error;
  }
};

// Update teacher profile (contact info, department, etc.)
export interface TeacherProfileData {
  displayName?: string;
  department?: string;
  roomNumber?: string;
  alternativeContact?: string;
  contactPreference?: 'email' | 'phone' | 'teams' | 'other';
  bio?: string;
  profilePictureUrl?: string;
}

export const updateTeacherProfile = async (
  teacherId: string,
  profileData: TeacherProfileData
): Promise<Parse.User> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== teacherId) {
      throw new Error('Unauthorized');
    }

    const userType = currentUser.get('userType');
    if (userType !== 'teacher') {
      throw new Error('Only teachers can update teacher profiles');
    }

    // Update allowed fields
    if (profileData.displayName !== undefined) {
      currentUser.set('displayName', profileData.displayName);
    }
    if (profileData.department !== undefined) {
      currentUser.set('teacherDepartment', profileData.department);
    }
    if (profileData.roomNumber !== undefined) {
      currentUser.set('teacherRoomNumber', profileData.roomNumber);
    }
    if (profileData.alternativeContact !== undefined) {
      currentUser.set('alternativeContact', profileData.alternativeContact);
    }
    if (profileData.contactPreference !== undefined) {
      currentUser.set('contactPreference', profileData.contactPreference);
    }
    if (profileData.bio !== undefined) {
      currentUser.set('teacherBio', profileData.bio);
    }
    if (profileData.profilePictureUrl !== undefined) {
      currentUser.set('profilePictureUrl', profileData.profilePictureUrl);
    }

    await currentUser.save();
    console.log(`Teacher profile ${teacherId} updated`);
    return currentUser;
  } catch (error) {
    console.error('Error updating teacher profile:', error);
    throw error;
  }
};

// Get teacher profile data
export const getTeacherProfile = async (teacherId: string): Promise<TeacherProfileData & { email: string; username: string }> => {
  try {
    const Parse = await getParseInstance();
    const currentUser = Parse.User.current();

    if (!currentUser || currentUser.id !== teacherId) {
      throw new Error('Unauthorized');
    }

    return {
      email: currentUser.get('email') || '',
      username: currentUser.get('username') || '',
      displayName: currentUser.get('displayName') || '',
      department: currentUser.get('teacherDepartment') || '',
      roomNumber: currentUser.get('teacherRoomNumber') || '',
      alternativeContact: currentUser.get('alternativeContact') || '',
      contactPreference: currentUser.get('contactPreference') || 'email',
      bio: currentUser.get('teacherBio') || '',
      profilePictureUrl: currentUser.get('profilePictureUrl') || '',
    };
  } catch (error) {
    console.error('Error fetching teacher profile:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------------
// Student Portal Functions (Public)
// ----------------------------------------------------------------------------

export const getPublicSchoolOpportunities = async (schoolSlug: string): Promise<Array<{
  opportunity: Parse.Object;
  teachers: Array<{
    name: string;
    email: string;
    department?: string;
    roomNumber?: string;
    availability?: string;
    notes?: string;
  }>;
}>> => {
  try {
    const Parse = await getParseInstance();

    // Get school by slug
    const school = await getSchoolBySlug(schoolSlug);
    if (!school) {
      throw new Error('School not found');
    }

    // Get all visible school opportunities
    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const query = new Parse.Query(SchoolOpportunity);

    query.equalTo('school', school);
    query.equalTo('isVisible', true);
    query.include('opportunity');
    query.descending('createdAt');

    const schoolOpportunities = await query.find();

    // If no opportunities, return empty array early
    if (schoolOpportunities.length === 0) {
      return [];
    }

    // BATCH QUERY: Get ALL teacher signups for ALL opportunities in ONE query
    // This fixes the N+1 query problem (was making 1 query per opportunity)
    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const signupQuery = new Parse.Query(TeacherSignup);
    signupQuery.containedIn('schoolOpportunity', schoolOpportunities);
    signupQuery.equalTo('isActive', true);
    signupQuery.include('teacher');

    const allSignups = await signupQuery.find();

    // Group signups by schoolOpportunity ID for O(1) lookup
    const signupsByOpportunityId: Record<string, Parse.Object[]> = {};
    for (const signup of allSignups) {
      const soId = signup.get('schoolOpportunity')?.id;
      if (soId) {
        if (!signupsByOpportunityId[soId]) {
          signupsByOpportunityId[soId] = [];
        }
        signupsByOpportunityId[soId].push(signup);
      }
    }

    // Build results - include ALL visible opportunities (even without teachers)
    const results: Array<{
      opportunity: Parse.Object;
      teachers: Array<{
        name: string;
        email: string;
        department?: string;
        roomNumber?: string;
        availability?: string;
        notes?: string;
      }>;
    }> = [];

    for (const so of schoolOpportunities) {
      const opportunity = so.get('opportunity');

      // Skip if opportunity object is missing (data integrity issue)
      if (!opportunity) {
        console.warn(`SchoolOpportunity ${so.id} has no linked opportunity`);
        continue;
      }

      // Get signups for this opportunity (may be empty array)
      const signups = signupsByOpportunityId[so.id] || [];

      // Filter out any signups where teacher is undefined/null
      const validSignups = signups.filter((signup: Parse.Object) => {
        const teacher = signup.get('teacher');
        return teacher && teacher.id && teacher.get('email');
      });

      // Map valid signups to teacher info
      const teachers = validSignups.map((signup: Parse.Object) => {
        const teacher = signup.get('teacher');
        return {
          name: teacher.get('displayName') || teacher.get('username') || 'Teacher',
          email: teacher.get('email') || '',
          department: teacher.get('teacherDepartment'),
          roomNumber: signup.get('roomNumber'),
          availability: signup.get('availability'),
          notes: signup.get('notes')
        };
      });

      // Include opportunity even if no teachers have signed up yet
      results.push({
        opportunity,
        teachers
      });
    }

    return results;
  } catch (error) {
    console.error('Error fetching public school opportunities:', error);
    throw error;
  }
};

export const getStudentOpportunityDetail = async (
  schoolSlug: string,
  opportunityId: string
): Promise<{
  opportunity: Parse.Object;
  teachers: Array<{
    name: string;
    email: string;
    department?: string;
    roomNumber?: string;
    availability?: string;
    notes?: string;
  }>;
  school: Parse.Object;
}> => {
  try {
    const Parse = await getParseInstance();

    // Get school by slug
    const school = await getSchoolBySlug(schoolSlug);
    if (!school) {
      throw new Error('School not found');
    }

    // Get school opportunity
    const SchoolOpportunity = Parse.Object.extend('SchoolOpportunity');
    const Opportunity = Parse.Object.extend('Opportunity');
    const query = new Parse.Query(SchoolOpportunity);

    query.equalTo('school', school);
    query.equalTo('opportunity', Opportunity.createWithoutData(opportunityId));
    query.equalTo('isVisible', true);
    query.include('opportunity');

    const schoolOpportunity = await query.first();

    if (!schoolOpportunity) {
      throw new Error('Opportunity not found at this school');
    }

    // Get teacher signups
    const TeacherSignup = Parse.Object.extend('TeacherSignup');
    const signupQuery = new Parse.Query(TeacherSignup);
    signupQuery.equalTo('schoolOpportunity', schoolOpportunity);
    signupQuery.equalTo('isActive', true);
    signupQuery.include('teacher');

    const signups = await signupQuery.find();

    // Filter out any signups where teacher is undefined/null
    const validSignups = signups.filter((signup: Parse.Object) => {
      const teacher = signup.get('teacher');
      return teacher && teacher.id; // Ensure teacher exists and has an id
    });

    const teachers = validSignups.map((signup: Parse.Object) => {
      const teacher = signup.get('teacher');
      return {
        name: teacher.get('displayName') || teacher.get('username') || 'Teacher',
        email: teacher.get('email') || '',
        department: teacher.get('teacherDepartment'),
        roomNumber: signup.get('roomNumber'),
        availability: signup.get('availability'),
        notes: signup.get('notes')
      };
    });

    return {
      opportunity: schoolOpportunity.get('opportunity'),
      teachers,
      school
    };
  } catch (error) {
    console.error('Error fetching student opportunity detail:', error);
    throw error;
  }
};